import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useCallback } from 'react';
import { Field, FormSpy } from 'react-final-form';

import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getCatalogsKnmActTypesSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmActTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmActTypeSelectOption';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { KnmLateFieldsRemark } from 'KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

import styles from '../../ErknmActsItem.module.scss';
import { IKnmErknmFormViolatedRequirementsNpa } from '../../../../MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmViolatedRequirementsNpaItem } from '../../../../../KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { getKnmFormRef } from '../../../../../KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from '../../../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { IBooleanSelectOption } from '../../../../../KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { getFieldArrayErrorMessage } from '../../../../../CorePackage/MainModule/common/getFieldArrayErrorMessage';
import { ErknmFieldMark } from '../../../../MainModule/components/ErknmFieldMark';
import { useShowMore } from '../../../../../hooks/useShowMore';
import { Spinner } from '../../../../../UIPackage/MainModule/components/Spinner';

interface IErknmActsItemProps {
  addRequirementsNpa: (actFieldName: string, fieldName: string) => void;
  addViolatedMandatory: (initialValue: IRequirementsNpaInitValues, fieldName: string) => void;
  id: string;
  index: number;
  isWatchOnly: boolean;
  name: string;
  value: IKnmErknmFormAct;
  isDisabledByComplete: boolean;
  knmActType: number | null;
  lateFieldName: string;
}

interface IErknmActsItemStoreProps {
  formRef: IKnmFormRef | undefined;
  knmActTypesOptions: IKnmActTypeSelectOption[] | undefined;
}

const ErknmActsItemContentViolatedMandatoryRequirementsWithoutStore = ({
  addRequirementsNpa,
  addViolatedMandatory,
  id,
  index,
  isWatchOnly,
  name,
  value,
  formRef,
  knmActTypesOptions,
  isDisabledByComplete,
  knmActType,
  lateFieldName,
}: IErknmActsItemProps & IErknmActsItemStoreProps) => {
  const remove = formRef?.current.mutators.remove;
  const violationResolvedOptions = booleanSelectOptions.map(item => {
    if (item.id === 0) {
      return {
        ...item,
        byDefault: true,
      };
    }

    return item;
  });

  const isDisabledField = isWatchOnly || isDisabledByComplete;

  const violatedRequirementsNpaFieldName = `${name}.violatedMandatoryRequirements`;

  const removeViolatedRequirementsNpa = (npaIndex: number) => {
    remove && remove(violatedRequirementsNpaFieldName, npaIndex);
  };

  const handleIsViolatedMandatoryRequirementsChange = useCallback(
    (v: IBooleanSelectOption) => {
      if (!v.value) {
        formRef?.current.change(violatedRequirementsNpaFieldName as keyof IKnmForm);
      }
    },
    [formRef, knmActType, knmActTypesOptions],
  );

  return (
    <>
      <FieldBlock
        id={`${id}.isViolatedMandatoryRequirements`}
        label={
          <>
            Выявленные нарушения обязательных требований
            <ErknmFieldMark fieldName={`${name}.isViolatedMandatoryRequirements`} />
          </>
        }
        size="half"
        margin="top"
        tooltip={
          <KnmLateFieldsRemark
            lateFieldPath={`${lateFieldName}/isViolations`}
            fieldId={`${id}.isViolatedMandatoryRequirements`}
          />
        }
      >
        <Row>
          <SelectInputField
            className="shared-row-flex"
            name={`${name}.isViolatedMandatoryRequirements`}
            options={booleanSelectOptions}
            isClearable={false}
            disabled={isDisabledField}
            onChange={handleIsViolatedMandatoryRequirementsChange}
            size="medium"
          />
          {value.isViolatedMandatoryRequirements?.value === true && (
            <Button
              className="shared-btn-add"
              text={
                <>
                  Добавить
                  <ErknmFieldMark fieldName={violatedRequirementsNpaFieldName} />
                </>
              }
              type="button"
              variant="link"
              color="primary"
              disabled={isDisabledField}
              onClick={() => addRequirementsNpa(name, violatedRequirementsNpaFieldName)}
            />
          )}
        </Row>
      </FieldBlock>
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = getFieldArrayErrorMessage(props.errors?.acts?.[index]?.violatedMandatoryRequirements);
          const isFieldTouched = props.touched?.places;

          if (isFieldTouched && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name={violatedRequirementsNpaFieldName}>
        {({ fields }) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 10,
          });

          return (
            <>
              {visibleItems.map((fieldValue: IKnmErknmFormViolatedRequirementsNpa) => {
                const npaIndex =
                  fields.value?.findIndex((item: IKnmErknmFormViolatedRequirementsNpa) => item === fieldValue) ?? 0;
                const npaName = `${violatedRequirementsNpaFieldName}[${npaIndex}]`;

                return (
                  <Field key={npaName} name={npaName}>
                    {({ meta }) => {
                      const isFieldTouched = meta.touched;
                      const errorField = meta.error;
                      const filedId = `${id}.violatedMandatoryRequirements[${fieldValue.guid}]`;

                      return (
                        <KnmViolatedRequirementsNpaItem
                          value={fieldValue}
                          name={npaName}
                          watchOnly={isDisabledField}
                          isViolationFieldsWatchOnly={isWatchOnly}
                          onRemove={() => removeViolatedRequirementsNpa(npaIndex)}
                          addMandatory={addViolatedMandatory}
                          touched={isFieldTouched}
                          error={errorField}
                          formRef={formRef}
                          isMandatoryDisabled={false}
                          id={filedId}
                        />
                      );
                    }}
                  </Field>
                );
              })}
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
            </>
          );
        }}
      </FieldArray>
      <FieldBlock
        label={'Факт устранения выявленного нарушения'}
        margin="top"
        id={`${id}.isViolationResolved`}
        tooltip={
          <KnmLateFieldsRemark
            lateFieldPath={`${lateFieldName}/isViolationResolved`}
            fieldId={`${id}.isViolationResolved`}
          />
        }
      >
        <SelectInputField
          className={styles.ViolationResolvedSelect}
          name={`${name}.isViolationResolved`}
          options={violationResolvedOptions}
          size="medium"
          disabled={isDisabledField}
          defaultValue={violationResolvedOptions.find(
            item => typeof value?.isViolationResolved?.value !== 'boolean' && item.byDefault,
          )}
          isClearable={false}
        />
        {value?.isViolationResolved?.value && (
          <div className="shared-field-row">
            <TextareaField
              id={`${id}.isViolationResolvedNote`}
              className={styles.ViolationResolvedField}
              name={`${name}.isViolationResolvedNote`}
              disabled={isDisabledField}
              placeholder={
                <>
                  Сведения о факте устранения выявленного нарушения
                  <ErknmFieldMark fieldName={`${name}.isViolationResolvedNote`} />
                </>
              }
            />
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/isViolationResolvedNote`}
              fieldId={`${id}.isViolationResolvedNote`}
              isLeftPosition={true}
            />
          </div>
        )}
      </FieldBlock>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmActsItemStoreProps => ({
  formRef: getKnmFormRef(state),
  knmActTypesOptions: getCatalogsKnmActTypesSelectOptions(state),
});

export const ErknmActsItemContentViolatedMandatoryRequirements = connect(mapStateToProps)(
  ErknmActsItemContentViolatedMandatoryRequirementsWithoutStore,
);
